import React, { useState } from 'react';
import { 
  ChevronRight, 
  MapPin, 
  Navigation, 
  TrendingUp,
  Clock,
  DollarSign,
  Truck,
  AlertTriangle,
  RefreshCw,
  ExternalLink,
  MapIcon
} from 'lucide-react';
import { colors } from '../../config/colors';
import {
  getRiskScoreColor,
  formatDistance,
  formatDuration,
  formatCost,
  formatLastAnalyzed
} from '../../models/routeAnalysisModels';
import { getGoogleMapsRouteUrl } from '../../utils/geocoding';
import RouteDetailsDialog from './RouteDetailsDialog';

const RouteAnalysisCard = ({ 
  routeAnalysis, 
  onAnalyze, 
  analyzing = false,
  compact = false 
}) => {
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  // Only render if we have actual analysis data
  if (!routeAnalysis || !routeAnalysis.routeId || !routeAnalysis.distanceKm) {
    return null;
  }

  const riskColors = getRiskScoreColor(routeAnalysis.riskScore);
  const isStale = routeAnalysis.isStale();

  const openInGoogleMaps = () => {
    const startAddress = routeAnalysis.getStartFullAddress();
    const endAddress = routeAnalysis.getEndFullAddress();
    const url = getGoogleMapsRouteUrl(startAddress, endAddress);
    window.open(url, '_blank');
  };

  // Compact view (for lists): click opens details in a dialog
  if (compact) {
    return (
      <>
        <div
          className="rounded-lg border p-4 hover:shadow-md transition-all cursor-pointer"
          style={{ 
            borderColor: routeAnalysis.isActive ? colors.border.medium : colors.border.light,
            backgroundColor: colors.background.card
          }}
          onClick={() => setDetailDialogOpen(true)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 flex-1 min-w-0">
              <div 
                className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: colors.accent[100] }}
              >
                <Navigation className="h-5 w-5" style={{ color: colors.accent[600] }} />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-sm truncate" style={{ color: colors.text.primary }}>
                  {routeAnalysis.routeName}
                </h4>
                <div className="flex items-center space-x-3 mt-1">
                  <span className="text-xs" style={{ color: colors.text.secondary }}>
                    {routeAnalysis.distanceText || formatDistance(routeAnalysis.distanceKm)}
                  </span>
                  <span className="text-xs" style={{ color: colors.text.tertiary }}>•</span>
                  <span className="text-xs" style={{ color: colors.text.secondary }}>
                    {routeAnalysis.durationText || formatDuration(routeAnalysis.durationMinutes)}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2 flex-shrink-0">
              <span 
                className="px-3 py-1 rounded-full text-xs font-medium"
                style={{ 
                  backgroundColor: riskColors.bg,
                  color: riskColors.text
                }}
              >
                {routeAnalysis.riskLevel}
              </span>
              <ChevronRight className="h-4 w-4" style={{ color: colors.text.tertiary }} />
            </div>
          </div>
        </div>
        {detailDialogOpen && (
          <RouteDetailsDialog
            routeAnalysis={routeAnalysis}
            onClose={() => setDetailDialogOpen(false)}
            onAnalyze={onAnalyze}
            analyzing={analyzing}
          />
        )}
      </>
    );
  }

  // Full/Expanded view
  return (
    <div
      className="rounded-xl border-2 overflow-hidden transition-all"
      style={{ 
        borderColor: routeAnalysis.isActive ? riskColors.border : colors.border.light,
        backgroundColor: colors.background.card
      }}
    >
      {/* Header */}
      <div 
        className="p-5 flex items-start justify-between"
        style={{ 
          backgroundColor: routeAnalysis.isActive ? riskColors.bg : colors.gray[50]
        }}
      >
        <div className="flex items-start space-x-3 flex-1 min-w-0">
          <div 
            className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ 
              backgroundColor: routeAnalysis.isActive ? colors.accent[500] : colors.gray[400]
            }}
          >
            <Navigation className="h-6 w-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-bold" style={{ color: colors.text.primary }}>
              {routeAnalysis.routeName}
            </h3>
            {routeAnalysis.description && (
              <p className="text-sm mt-1" style={{ color: colors.text.secondary }}>
                {routeAnalysis.description}
              </p>
            )}
            {isStale && (
              <div className="flex items-center space-x-1 mt-2">
                <AlertTriangle className="h-3 w-3" style={{ color: colors.warning[600] }} />
                <span className="text-xs" style={{ color: colors.warning[600] }}>
                  Analysis is over 7 days old
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center space-x-2 flex-shrink-0 ml-3">
          <span 
            className="px-3 py-1.5 rounded-full text-sm font-semibold"
            style={{ 
              backgroundColor: riskColors.bg,
              color: riskColors.text,
              border: `2px solid ${riskColors.border}`
            }}
          >
            {routeAnalysis.riskLevel} RISK
          </span>
          {!routeAnalysis.isActive && (
            <span 
              className="px-3 py-1.5 rounded-full text-xs font-medium"
              style={{ 
                backgroundColor: colors.gray[200],
                color: colors.gray[700]
              }}
            >
              Inactive
            </span>
          )}
        </div>
      </div>

      {/* Route Path */}
      <div className="px-5 py-4 border-b" style={{ borderColor: colors.border.light }}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Start Point */}
          <div className="flex items-start space-x-3">
            <div 
              className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: colors.success[200] }}
            >
              <MapPin className="h-5 w-5" style={{ color: colors.success[700] }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium" style={{ color: colors.text.secondary }}>
                Starting Point
              </p>
              <p className="text-sm font-semibold mt-1" style={{ color: colors.text.primary }}>
                {routeAnalysis.startCity}, {routeAnalysis.startProvince}
              </p>
              {routeAnalysis.startAddress && (
                <p className="text-xs mt-0.5" style={{ color: colors.text.tertiary }}>
                  {routeAnalysis.startAddress}
                </p>
              )}
              {(routeAnalysis.startLat && routeAnalysis.startLng) && (
                <p className="text-xs mt-0.5 font-mono" style={{ color: colors.text.tertiary }}>
                  {routeAnalysis.startLat.toFixed(4)}, {routeAnalysis.startLng.toFixed(4)}
                </p>
              )}
            </div>
          </div>

          {/* End Point */}
          <div className="flex items-start space-x-3">
            <div 
              className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: colors.danger[200] }}
            >
              <MapPin className="h-5 w-5" style={{ color: colors.danger[700] }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium" style={{ color: colors.text.secondary }}>
                Destination
              </p>
              <p className="text-sm font-semibold mt-1" style={{ color: colors.text.primary }}>
                {routeAnalysis.endCity}, {routeAnalysis.endProvince}
              </p>
              {routeAnalysis.endAddress && (
                <p className="text-xs mt-0.5" style={{ color: colors.text.tertiary }}>
                  {routeAnalysis.endAddress}
                </p>
              )}
              {(routeAnalysis.endLat && routeAnalysis.endLng) && (
                <p className="text-xs mt-0.5 font-mono" style={{ color: colors.text.tertiary }}>
                  {routeAnalysis.endLat.toFixed(4)}, {routeAnalysis.endLng.toFixed(4)}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Distance */}
        <div 
          className="p-3 rounded-lg"
          style={{ backgroundColor: colors.info[50] }}
        >
          <div className="flex items-center space-x-2 mb-1">
            <TrendingUp className="h-4 w-4" style={{ color: colors.info[600] }} />
            <span className="text-xs font-medium" style={{ color: colors.text.secondary }}>
              Distance
            </span>
          </div>
          <p className="text-lg font-bold" style={{ color: colors.info[700] }}>
            {routeAnalysis.distanceText || formatDistance(routeAnalysis.distanceKm)}
          </p>
        </div>

        {/* Duration */}
        <div 
          className="p-3 rounded-lg"
          style={{ backgroundColor: colors.primary[50] }}
        >
          <div className="flex items-center space-x-2 mb-1">
            <Clock className="h-4 w-4" style={{ color: colors.primary[600] }} />
            <span className="text-xs font-medium" style={{ color: colors.text.secondary }}>
              Duration
            </span>
          </div>
          <p className="text-lg font-bold" style={{ color: colors.primary[700] }}>
            {routeAnalysis.durationText || formatDuration(routeAnalysis.durationMinutes)}
          </p>
          {routeAnalysis.durationInTrafficText && (
            <p className="text-xs mt-1" style={{ color: colors.text.tertiary }}>
              {routeAnalysis.durationInTrafficText} in traffic
            </p>
          )}
        </div>

        {/* Cost */}
        <div 
          className="p-3 rounded-lg"
          style={{ backgroundColor: colors.success[50] }}
        >
          <div className="flex items-center space-x-2 mb-1">
            <DollarSign className="h-4 w-4" style={{ color: colors.success[600] }} />
            <span className="text-xs font-medium" style={{ color: colors.text.secondary }}>
              Est. Cost
            </span>
          </div>
          <p className="text-lg font-bold" style={{ color: colors.success[700] }}>
            {formatCost(routeAnalysis.estimatedCost)}
          </p>
        </div>

        {/* Risk Score */}
        <div 
          className="p-3 rounded-lg"
          style={{ backgroundColor: riskColors.bg }}
        >
          <div className="flex items-center space-x-2 mb-1">
            <AlertTriangle className="h-4 w-4" style={{ color: riskColors.text }} />
            <span className="text-xs font-medium" style={{ color: colors.text.secondary }}>
              Risk Score
            </span>
          </div>
          <p className="text-lg font-bold" style={{ color: riskColors.text }}>
            {routeAnalysis.riskScore.toFixed(1)}/100
          </p>
        </div>
      </div>

      {/* Risk Score Progress Bar */}
      <div className="px-5 pb-5">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-xs font-medium" style={{ color: colors.text.secondary }}>
            Risk Assessment
          </span>
          <span className="text-xs font-semibold" style={{ color: riskColors.text }}>
            {routeAnalysis.riskLevel}
          </span>
        </div>
        <div className="w-full h-3 rounded-full overflow-hidden" style={{ backgroundColor: colors.gray[200] }}>
          <div 
            className="h-full rounded-full transition-all duration-500"
            style={{ 
              width: `${routeAnalysis.riskScore}%`,
              backgroundColor: riskColors.progressBar
            }}
          />
        </div>
      </div>

      {/* Risk Factors */}
      {routeAnalysis.riskFactors && routeAnalysis.riskFactors.length > 0 && (
        <div className="px-5 pb-4">
          <h4 className="text-sm font-semibold mb-2" style={{ color: colors.text.primary }}>
            Risk Factors
          </h4>
          <ul className="space-y-1">
            {routeAnalysis.riskFactors.map((factor, index) => (
              <li key={index} className="flex items-start space-x-2">
                <span className="text-xs mt-0.5" style={{ color: riskColors.text }}>•</span>
                <span className="text-xs" style={{ color: colors.text.secondary }}>
                  {factor}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Transportation Details */}
      {(routeAnalysis.carrier || routeAnalysis.vehicleType) && (
        <div className="px-5 pb-4 border-t pt-4" style={{ borderColor: colors.border.light }}>
          <div className="flex items-center space-x-4">
            {routeAnalysis.carrier && (
              <div className="flex items-center space-x-2">
                <Truck className="h-4 w-4" style={{ color: colors.text.tertiary }} />
                <span className="text-sm" style={{ color: colors.text.secondary }}>
                  <span className="font-medium" style={{ color: colors.text.primary }}>
                    {routeAnalysis.carrier}
                  </span>
                </span>
              </div>
            )}
            {routeAnalysis.vehicleType && (
              <div className="flex items-center space-x-2">
                <span className="text-xs px-2 py-1 rounded" style={{ 
                  backgroundColor: colors.gray[100],
                  color: colors.text.secondary
                }}>
                  {routeAnalysis.vehicleType}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Footer Actions */}
      <div 
        className="px-5 py-4 border-t flex items-center justify-between"
        style={{ 
          borderColor: colors.border.light,
          backgroundColor: colors.gray[50]
        }}
      >
        <div className="text-xs" style={{ color: colors.text.tertiary }}>
          Last analyzed: {formatLastAnalyzed(routeAnalysis.lastAnalyzed)}
        </div>
        <div className="flex items-center space-x-2">
          {onAnalyze && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onAnalyze(routeAnalysis.routeId);
              }}
              disabled={analyzing}
              className="px-4 py-2 rounded-lg text-sm font-medium flex items-center space-x-2 transition-all"
              style={{
                backgroundColor: analyzing ? colors.gray[200] : colors.secondary[50],
                color: analyzing ? colors.gray[500] : colors.secondary[700],
                border: `1px solid ${analyzing ? colors.gray[300] : colors.secondary[300]}`
              }}
            >
              <RefreshCw className={`h-4 w-4 ${analyzing ? 'animate-spin' : ''}`} />
              <span>{analyzing ? 'Analyzing...' : 'Refresh Analysis'}</span>
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              openInGoogleMaps();
            }}
            className="px-4 py-2 rounded-lg text-sm font-medium flex items-center space-x-2 transition-all hover:shadow-md"
            style={{
              backgroundColor: colors.primary[500],
              color: 'white'
            }}
          >
            <MapIcon className="h-4 w-4" />
            <span>View in Maps</span>
            <ExternalLink className="h-3 w-3" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default RouteAnalysisCard;

